/* eslint-disable @typescript-eslint/no-explicit-any */
import { useWebSocketContext } from '@/context/websocket-context';
import { useAppDispatch } from '@/store';
import {
  applyLeadListTotalCount,
  invalidateLeadListCompaniesCache,
  invalidateLeadListLeadsCache,
  leadListsApi,
  updateLeadListLeadsCache,
} from '@/store/features/lead-lists.feature';
import { LeadListLeads, LeadWithProspect } from '@/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  isActionColumnLeadUpdateMessage,
  isActionColumnProgressMessage,
  isBulkOperationCompleteMessage,
  isBulkProspectingMessage,
  isCompanyEnrichmentProgressMessage,
  isCreditUpdateMessage,
  isCrmLookupLeadUpdateMessage,
  isCrmLookupMessage,
  isEnrichmentMessage,
  isFindPeopleProgressMessage,
  isLeadCreatedMessage,
  isLeadDeletedMessage,
  isLeadListUpdateMessage,
  isLeadUpdatedMessage,
  isPersonalEmailProgressMessage,
  isSmartColumnCompanyLeadUpdateMessage,
  isSmartColumnCompanyUpdateMessage,
  isSmartColumnLeadUpdateMessage,
  isSmartColumnMessage,
  type ActionColumnLeadUpdateMessage,
  type ActionColumnProgressMessage,
  type BulkProspectingMessage,
  type CreditUpdateMessage,
  type CrmLookupLeadUpdateMessage,
  type CrmLookupUpdateMessage,
  type EnrichmentMessage,
  type LeadListUpdateMessage,
  type LeadsWebSocketMessage,
  type SmartColumnCompanyLeadUpdateMessage,
  type SmartColumnCompanyUpdateMessage,
  type SmartColumnLeadUpdateMessage,
  type SmartColumnMessage,
  type WebSocketMessage,
} from './use-websocket.hook';

export type {
  ActionColumnLeadUpdateMessage,
  ActionColumnProgressMessage,
  BulkProspectingMessage,
  CreditUpdateMessage,
  CrmLookupLeadUpdateMessage,
  CrmLookupUpdateMessage,
  EnrichmentMessage,
  LeadListUpdateMessage,
  LeadsWebSocketMessage,
  SmartColumnCompanyLeadUpdateMessage,
  SmartColumnCompanyUpdateMessage,
  SmartColumnLeadUpdateMessage,
  SmartColumnMessage,
  WebSocketMessage,
};

export {
  isBulkOperationCompleteMessage,
  isBulkProspectingMessage,
  isCompanyEnrichmentProgressMessage,
  isCreditUpdateMessage,
  isCrmLookupLeadUpdateMessage,
  isCrmLookupMessage,
  isEnrichmentMessage,
  isFindPeopleProgressMessage,
  isLeadCreatedMessage,
  isActionColumnLeadUpdateMessage,
  isActionColumnProgressMessage,
  isLeadDeletedMessage,
  isLeadListUpdateMessage,
  isLeadUpdatedMessage,
  isPersonalEmailProgressMessage,
  isSmartColumnCompanyLeadUpdateMessage,
  isSmartColumnCompanyUpdateMessage,
  isSmartColumnLeadUpdateMessage,
  isSmartColumnMessage,
};

/**
 * Resolve the set of lists a ``lead_list_update`` / ``bulk_operation_complete``
 * frame touched, accepting BOTH payload shapes during the transition:
 *   - plural ``affected_list_ids`` (the new BE per-operation service emits this,
 *     the SSOT for "which lists did this op touch"), and
 *   - singular ``lead_list_id`` (every historical emitter), treated as a
 *     1-element list.
 * Exported for unit testing.
 */
export const resolveAffectedListIds = (
  affectedListIds: unknown,
  singularListId: unknown
): string[] => {
  const ids = new Set<string>();
  if (Array.isArray(affectedListIds)) {
    for (const id of affectedListIds) {
      if (typeof id === 'string' && id) ids.add(id);
    }
  }
  if (typeof singularListId === 'string' && singularListId) {
    ids.add(singularListId);
  }
  return [...ids];
};

const invalidateLeadListIndex = (
  dispatch: (action: unknown) => void,
  listIds: Iterable<string>
): void => {
  dispatch(
    leadListsApi.util.invalidateTags([
      ...[...listIds].map((id) => ({ type: 'LeadList' as const, id })),
      { type: 'LeadList', id: 'LIST' },
    ])
  );
};

/**
 * Invalidate the FULL RTK Query tag set for every list a Copilot/other mutation
 * touched, independent of which list is currently open. This is the SOLE refresh
 * authority for list mutations (the SSE display.type Sets were deleted). For
 * each id it invalidates {LeadListLeads,id} + {LeadListCompanies,id} +
 * {LeadList,id} + {LeadList,'LIST'} (the sidebar index); `index: false` skips
 * the last two. Exported for testing.
 */
export const invalidateForLeadListUpdate = (
  dispatch: (action: unknown) => void,
  listIds: string[],
  { index = true }: { index?: boolean } = {}
): void => {
  for (const id of listIds) {
    invalidateLeadListLeadsCache(dispatch, id);
    // Not invalidateLeadListCompaniesCache: it also refreshes the index,
    // which mid-burst flushes leave alone.
    dispatch(
      leadListsApi.util.invalidateTags([{ type: 'LeadListCompanies', id }])
    );
  }
  // The index provides {LeadList,id} for every list it holds, so a list's own
  // tag refetches it too: both go together, only when the index is due.
  if (index && listIds.length > 0) {
    invalidateLeadListIndex(dispatch, listIds);
  }
};

/**
 * App-shell-level listener that makes ``lead_list_update`` /
 * ``bulk_operation_complete`` the SOLE refresh authority for list mutations
 * regardless of which surface is open — including the home / Copilot view,
 * which has no ``$listId`` route and therefore never mounts
 * {@link useLeadsWebSocket}.
 *
 * STAGE C deleted the SSE ``display.type`` Set machinery, leaving the
 * per-list-page handler in {@link useLeadsWebSocket} as the only code path that
 * called {@link invalidateForLeadListUpdate}. That handler is mounted ONLY by
 * the lead-list-leads container, so Copilot operations issued from the home
 * view (create/import/move/copy/enrich/run_search/quick_action) silently left
 * the sidebar list index, the companies grid, and any target-list leads stale
 * until a hard refresh.
 *
 * Mount this once at an always-rendered shell level (it lives in
 * ``AppEffects``). It listens on the ``leads`` category, accepts BOTH payload
 * shapes via {@link resolveAffectedListIds} (plural ``affected_list_ids`` and
 * singular ``lead_list_id`` / ``list_id``), and invalidates the full RTK Query
 * tag set for every touched list.
 *
 * This is intentionally independent of the open-list handler: the per-page
 * handler still owns the in-place cache patching for the currently-open list,
 * while this global listener guarantees cross-list / no-list-open refreshes.
 * Invalidations are idempotent, so the overlap on the open list is harmless.
 */
export const useLeadListUpdateRefresh = (): void => {
  const dispatch = useAppDispatch();
  const { subscribe } = useWebSocketContext();

  useEffect(() => {
    // Throttled per list: every enriched row of a bulk run sends its own
    // lead_list_update, so a 500-row run refetched the leads, companies,
    // list and sidebar index ~500 times back to back (Replay: a laptop
    // slowing to a crawl during an import). The first update in a quiet
    // period still refreshes at once (a single Clu action stays instant);
    // a burst lands in one refresh per window, and a run's
    // bulk_operation_complete flushes straight away.
    //
    // The sidebar index ({LeadList,'LIST'}) aggregates every list's email
    // status live: ~365 ms of SQL with a 100k list in the org. Mid-burst
    // flushes skip it; it refreshes once the burst goes quiet, or at once on
    // bulk_operation_complete.
    const pending = new Set<string>();
    let timer: ReturnType<typeof setTimeout> | null = null;
    // Lists a mid-burst flush refreshed without their {LeadList,id} tags.
    const indexStale = new Set<string>();
    const flush = ({ index }: { index: boolean }) => {
      if (pending.size === 0) {
        if (index && indexStale.size > 0) {
          invalidateLeadListIndex(dispatch, indexStale);
        }
        if (index) indexStale.clear();
        return;
      }
      const ids = [...pending];
      pending.clear();
      if (index) {
        invalidateForLeadListUpdate(dispatch, [
          ...new Set([...ids, ...indexStale]),
        ]);
        indexStale.clear();
      } else {
        invalidateForLeadListUpdate(dispatch, ids, { index: false });
        for (const id of ids) indexStale.add(id);
      }
    };
    const onWindowEnd = () => {
      if (pending.size === 0) {
        timer = null;
        flush({ index: true });
        return;
      }
      flush({ index: false });
      timer = setTimeout(onWindowEnd, LEAD_LIST_REFRESH_WINDOW_MS);
    };
    const queue = (ids: string[], { now }: { now: boolean }) => {
      for (const id of ids) pending.add(id);
      if (now) return flush({ index: true });
      if (timer) return;
      flush({ index: true });
      timer = setTimeout(onWindowEnd, LEAD_LIST_REFRESH_WINDOW_MS);
    };

    const unsubscribe = subscribe('leads', (msg) => {
      if (isLeadListUpdateMessage(msg)) {
        queue(resolveAffectedListIds(msg.affected_list_ids, msg.lead_list_id), {
          now: false,
        });
        return;
      }
      if (isBulkOperationCompleteMessage(msg)) {
        queue(resolveAffectedListIds(undefined, msg.list_id), { now: true });
      }
    });
    return () => {
      unsubscribe();
      if (timer) clearTimeout(timer);
      flush({ index: true });
    };
  }, [subscribe, dispatch]);
};

/** At most one refresh of a list per this window while updates keep coming. */
export const LEAD_LIST_REFRESH_WINDOW_MS = 2000;

type SmartColumnSocketMessage =
  | SmartColumnMessage
  | SmartColumnLeadUpdateMessage
  | CrmLookupUpdateMessage
  | CrmLookupLeadUpdateMessage;

/**
 * Company-scope smart-column socket messages. Distinct union from the
 * lead-scope {@link SmartColumnSocketMessage} so company payloads can never
 * be routed into the lead handler.
 */
type CompanySmartColumnSocketMessage =
  | SmartColumnCompanyUpdateMessage
  | SmartColumnCompanyLeadUpdateMessage;

type ActionColumnSocketMessage =
  | ActionColumnLeadUpdateMessage
  | ActionColumnProgressMessage;

/**
 * Hook for subscribing to smart column WebSocket updates.
 * Uses the unified WebSocket connection via context.
 *
 * Pass `onMessage` to get every frame; `lastMessage` then stays null. Storing
 * it too re-rendered the caller on every frame (other lists' frames included),
 * and frames landing in one tick collapsed into the last one. The same holds
 * for the action-column, company and enrichment hooks below.
 */
export const useSmartColumnWebSocket = (
  onMessage?: (message: SmartColumnSocketMessage) => void
) => {
  const { isConnected, subscribe } = useWebSocketContext();
  const [lastMessage, setLastMessage] =
    useState<SmartColumnSocketMessage | null>(null);

  // Store callback in ref to avoid re-subscriptions
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    const unsubscribe = subscribe('smartColumns', (msg) => {
      if (
        isSmartColumnMessage(msg) ||
        isSmartColumnLeadUpdateMessage(msg) ||
        isCrmLookupMessage(msg) ||
        isCrmLookupLeadUpdateMessage(msg)
      ) {
        if (onMessageRef.current)
          onMessageRef.current(msg as SmartColumnSocketMessage);
        else setLastMessage(msg as SmartColumnSocketMessage);
      }
    });

    return unsubscribe;
  }, [subscribe]);

  return {
    isConnected,
    lastMessage,
    // No-op for backwards compatibility - context handles reconnection
    reconnect: () => {},
    sendMessage: () => {},
  };
};

/**
 * Hook for subscribing to COMPANY-scope smart column WebSocket updates.
 *
 * Shares the same ``smartColumns`` context category as
 * {@link useSmartColumnWebSocket} but only forwards the company message pair
 * (``smart_column_company_update`` / ``smart_column_company_lead_update``).
 * The lead hook's type-guard filter rejects these, so the two streams stay
 * fully separate — a company payload never reaches the lead handler and
 * vice-versa.
 */
export const useCompanySmartColumnWebSocket = (
  onMessage?: (message: CompanySmartColumnSocketMessage) => void
) => {
  const { isConnected, subscribe } = useWebSocketContext();
  const [lastMessage, setLastMessage] =
    useState<CompanySmartColumnSocketMessage | null>(null);

  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    const unsubscribe = subscribe('smartColumns', (msg) => {
      if (
        isSmartColumnCompanyUpdateMessage(msg) ||
        isSmartColumnCompanyLeadUpdateMessage(msg)
      ) {
        if (onMessageRef.current)
          onMessageRef.current(msg as CompanySmartColumnSocketMessage);
        else setLastMessage(msg as CompanySmartColumnSocketMessage);
      }
    });

    return unsubscribe;
  }, [subscribe]);

  return {
    isConnected,
    lastMessage,
    // No-op for backwards compatibility - context handles reconnection
    reconnect: () => {},
    sendMessage: () => {},
  };
};

/**
 * Hook for subscribing to action column WebSocket updates.
 * Uses the unified WebSocket connection via context.
 */
export const useActionColumnWebSocket = (
  onMessage?: (message: ActionColumnSocketMessage) => void
) => {
  const { isConnected, subscribe } = useWebSocketContext();
  const [lastMessage, setLastMessage] =
    useState<ActionColumnSocketMessage | null>(null);

  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    const unsubscribe = subscribe('actionColumns', (msg) => {
      if (
        isActionColumnLeadUpdateMessage(msg) ||
        isActionColumnProgressMessage(msg)
      ) {
        if (onMessageRef.current)
          onMessageRef.current(msg as ActionColumnSocketMessage);
        else setLastMessage(msg as ActionColumnSocketMessage);
      }
    });

    return unsubscribe;
  }, [subscribe]);

  return {
    isConnected,
    lastMessage,
  };
};

/**
 * Hook for subscribing to enrichment WebSocket updates.
 * Uses the unified WebSocket connection via context.
 */
export const useEnrichmentWebSocket = (
  onMessage?: (message: WebSocketMessage) => void
) => {
  const { isConnected, subscribe } = useWebSocketContext();
  const [lastMessage, setLastMessage] = useState<
    EnrichmentMessage | BulkProspectingMessage | null
  >(null);

  // Store callback in ref to avoid re-subscriptions
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;

  useEffect(() => {
    const unsubscribe = subscribe('enrichment', (msg) => {
      if (isEnrichmentMessage(msg) || isBulkProspectingMessage(msg)) {
        if (onMessageRef.current) onMessageRef.current(msg);
        else setLastMessage(msg as EnrichmentMessage | BulkProspectingMessage);
      }
    });

    return unsubscribe;
  }, [subscribe]);

  return {
    isConnected,
    lastMessage,
    // No-op for backwards compatibility - context handles reconnection
    reconnect: () => {},
    sendMessage: () => {},
  };
};

type UseLeadsWebSocketOptions = {
  listId?: string;
  page?: number;
  pageSize?: number;
  enabled?: boolean;
  onReconnect?: () => void;
};

/**
 * Hook for subscribing to leads WebSocket updates.
 * Uses the unified WebSocket connection via context.
 * Automatically manages list subscriptions.
 */
export const useLeadsWebSocket = ({
  listId,
  page = 1,
  pageSize = 100,
  enabled = true,
  onReconnect,
}: UseLeadsWebSocketOptions) => {
  const dispatch = useAppDispatch();
  const {
    isConnected,
    isLeader,
    subscribe,
    subscribeToList,
    unsubscribeFromList,
  } = useWebSocketContext();

  const listIdRef = useRef<string | undefined>(listId);
  const pageRef = useRef(page);
  const pageSizeRef = useRef(pageSize);
  const lastListIdRef = useRef<string | null>(null);
  const wasConnectedRef = useRef(false);
  const onReconnectRef = useRef(onReconnect);

  useEffect(() => {
    listIdRef.current = listId;
  }, [listId]);

  useEffect(() => {
    pageRef.current = page;
  }, [page]);

  useEffect(() => {
    pageSizeRef.current = pageSize;
  }, [pageSize]);

  useEffect(() => {
    onReconnectRef.current = onReconnect;
  }, [onReconnect]);

  const handleLeadsMessage = useCallback(
    (message: LeadsWebSocketMessage) => {
      const currentListId = listIdRef.current;
      // lead_list_update uses lead_list_id instead of list_id
      // find_people_progress / lead_list_companies_updated /
      // personal_email_progress use payload.lead_list_id
      const messageListId =
        message.type === 'lead_list_update'
          ? message.lead_list_id
          : message.type === 'find_people_progress' ||
              message.type === 'lead_list_companies_updated' ||
              message.type === 'personal_email_progress' ||
              message.type === 'company_enrichment_progress'
            ? message.payload.lead_list_id
            : message.list_id;
      if (import.meta.env.DEV) {
        console.log(
          '[LeadsWS] Received leads message:',
          message.type,
          'list_id:',
          messageListId,
          'current:',
          currentListId
        );
      }

      // lead_list_update / bulk_operation_complete are handled by the
      // globally-mounted {@link useLeadListUpdateRefresh} (AppEffects), the
      // SOLE refresh authority for list mutations. They intentionally fall
      // through here (mismatched lists early-return below; the open list hits
      // the switch's no-op default) — do NOT re-add a per-page duplicate.

      if (!currentListId || messageListId !== currentListId) {
        if (import.meta.env.DEV) {
          console.log('[LeadsWS] Ignoring message - list_id mismatch');
        }
        return;
      }
      const currentPage = pageRef.current;
      const currentPageSize = pageSizeRef.current;

      const updateCurrentPage = (updater: (draft: LeadListLeads) => void) => {
        const cacheArgs = {
          listId: currentListId,
          page: currentPage,
          pageSize: currentPageSize,
        };
        if (import.meta.env.DEV) {
          console.log('[LeadsWS] Updating cache with args:', cacheArgs);
        }
        updateLeadListLeadsCache(dispatch, cacheArgs, updater);
      };

      switch (message.type) {
        case 'lead_created': {
          if (import.meta.env.DEV) {
            console.log(
              '[LeadsWS] Processing lead_created, page:',
              currentPage,
              'pageSize:',
              currentPageSize
            );
          }
          updateCurrentPage((draft) => {
            if (currentPage === 1 && draft.leads) {
              const incomingLead = message.lead as LeadWithProspect;
              if (import.meta.env.DEV) {
                console.log('[LeadsWS] Adding lead to list:', incomingLead.id);
              }
              const filtered = draft.leads.filter(
                (lead) => lead.id !== incomingLead.id
              );
              draft.leads = [incomingLead, ...filtered];
              if (draft.leads.length > currentPageSize) {
                draft.leads = draft.leads.slice(0, currentPageSize);
              }
            } else if (import.meta.env.DEV) {
              console.log(
                '[LeadsWS] Not on page 1 or no leads in draft, skipping insert'
              );
            }
            applyLeadListTotalCount(draft, message.total_count);
          });
          break;
        }
        case 'lead_updated': {
          updateCurrentPage((draft) => {
            if (!draft.leads?.length) return;
            const index = draft.leads.findIndex(
              (lead) => lead.id === message.lead_id
            );
            if (index === -1) return;
            draft.leads[index] = {
              ...draft.leads[index],
              ...message.changes,
            };
          });
          break;
        }
        case 'lead_deleted': {
          updateCurrentPage((draft) => {
            if (draft.leads?.length) {
              draft.leads = draft.leads.filter(
                (lead) => !message.lead_ids.includes(lead.id)
              );
            }
            applyLeadListTotalCount(draft, message.total_count);
          });
          break;
        }
        case 'find_people_progress': {
          const { payload } = message;
          if (payload?.status === 'completed') {
            const listToInvalidate = payload.lead_list_id || currentListId;
            if (listToInvalidate) {
              invalidateLeadListLeadsCache(dispatch, listToInvalidate);
              invalidateLeadListCompaniesCache(dispatch, listToInvalidate);
            }
          }
          break;
        }
        case 'personal_email_progress': {
          // Personal emails write onto existing lead rows. Refetch the
          // current page on any terminal frame so the new personal_email
          // values show up. ``completed_with_errors`` still refetches -
          // partial hits may have landed even when some leads failed.
          const { payload } = message;
          if (
            payload?.status === 'completed' ||
            payload?.status === 'completed_with_errors'
          ) {
            const listToInvalidate = payload.lead_list_id || currentListId;
            if (listToInvalidate) {
              invalidateLeadListLeadsCache(dispatch, listToInvalidate);
            }
          }
          break;
        }
        case 'company_enrichment_progress': {
          const { payload } = message;
          // Firmographic fields are written onto company records, so refresh
          // the companies cache once the run settles. Leads derive their
          // company display data, so refresh those too.
          if (
            payload?.status === 'completed' ||
            payload?.status === 'completed_with_errors'
          ) {
            const listToInvalidate = payload.lead_list_id || currentListId;
            if (listToInvalidate) {
              invalidateLeadListLeadsCache(dispatch, listToInvalidate);
              invalidateLeadListCompaniesCache(dispatch, listToInvalidate);
            }
          }
          break;
        }
        default:
          break;
      }
    },
    [dispatch]
  );

  useEffect(() => {
    if (!enabled) return;

    const unsubscribe = subscribe('leads', (msg) => {
      if (
        isLeadCreatedMessage(msg) ||
        isLeadUpdatedMessage(msg) ||
        isLeadDeletedMessage(msg) ||
        isBulkOperationCompleteMessage(msg) ||
        isLeadListUpdateMessage(msg) ||
        isFindPeopleProgressMessage(msg) ||
        isPersonalEmailProgressMessage(msg) ||
        isCompanyEnrichmentProgressMessage(msg)
      ) {
        handleLeadsMessage(msg as LeadsWebSocketMessage);
      }
    });

    return unsubscribe;
  }, [enabled, subscribe, handleLeadsMessage]);

  useEffect(() => {
    if (!enabled) return;

    const previous = lastListIdRef.current;

    if (import.meta.env.DEV) {
      console.log(
        '[LeadsWS] Subscription effect running, listId:',
        listId,
        'previous:',
        previous
      );
    }

    if (previous && previous !== listId) {
      unsubscribeFromList(previous);
    }

    if (listId) {
      if (import.meta.env.DEV) {
        console.log('[LeadsWS] Subscribing to list:', listId);
      }
      subscribeToList(listId);
    }

    lastListIdRef.current = listId ?? null;

    return () => {
      if (listId) {
        unsubscribeFromList(listId);
      }
    };
  }, [enabled, listId, subscribeToList, unsubscribeFromList]);

  useEffect(() => {
    if (!enabled) return;

    if (isConnected && !wasConnectedRef.current) {
      onReconnectRef.current?.();
    }

    wasConnectedRef.current = isConnected;
  }, [enabled, isConnected]);

  return {
    isConnected: enabled ? isConnected : false,
    isLeader,
  };
};
